/* Copyright (c) 2022 MOCA Systems, Inc.  All rights reserved. */
import firebase from 'firebase/compat/app';
import { combineLatest, MonoTypeOperatorFunction, Observable } from 'rxjs';
import { catchError, debounceTime, map, switchMap, take, tap } from 'rxjs/operators';
import { delayedRetry, DelayedRetryError } from 'src/app/plain-javascript/services/rxjs-internal-extensions';
import { logger } from '@touchplan/logging';

/**
 * A check to try and determine if a fancyFirebaseList has loaded. Because such a thing _basically_ can't be reliable. The check
 * has a heuristic algorithm that should work... _most_ of the time. The check works by
 * - listening on the source observable for its size
 * - fetching the entire node that the list represents once via native firebase (firebase caching should limit duplicate fetches)
 * - after the snapshot is returned, a timer is started that re-checks the list size for 2 seconds until the list size is at least the size
 *   of the snapshot
 * - if it never loads an error will be thrown
 * This function can be piped to the _goodEnough_ operator below to suppress the error when this loading is not mission critical
 * @param ref
 * @param list
 */
export function loaded$<T>(ref: firebase.database.Reference, list: Observable<Map<string, T>>) {
	return combineLatest([list, exportFunctions.getSnapshotChildCount$(ref)]).pipe(
		delayedRetry(([listEmission, count]) => listEmission.size >= count, 200, 10),
		debounceTime(20),
		map((comb) => comb[1]),
		take(1)
	);
}

/**
 * Custom operator to be paired with delayedRetry. Allows for suppressing the "not loaded" error as long as a provided check passes
 * @param cb Optional callback which will be passed the last emission. Return true to pass the check. The check will pass automatically if
 * no callback is provided.
 */
export function goodEnough<T>(cb?: (val: T) => boolean): MonoTypeOperatorFunction<T> {
	return function goodEnoughImplementation(source) {
		return source.pipe(
			catchError((err: DelayedRetryError, caught) => {
				if (!err.source) {
					logger.error('Original Error', err);
					throw new Error('source not found. This can only handle a DelayedRetryError');
				}
				return (err.source as typeof caught).pipe(
					tap((val) => {
						if (cb && !cb(val)) {
							throw new Error('good enough check failed');
						}
					})
				);
			})
		);
	};
}

/**
 * Fetches a chunk of data from firebase, returns the count and completes. Special care is taken to maintain the zone
 */
function getSnapshotChildCount$(ref: firebase.database.Reference): Observable<number> {
	const originalZone = Zone.current;
	return new Observable<number>((subscriber) => {
		ref.once(
			'value',
			(val) =>
				originalZone.run(
					(data: firebase.database.DataSnapshot) => {
						subscriber.next(data.numChildren());
						subscriber.complete();
					},
					undefined,
					[val]
				),
			(unzonedErr) =>
				originalZone.run(
					(err) => {
						if (err) {
							subscriber.error(err);
						}
					},
					undefined,
					[unzonedErr]
				)
		);
	});
}

//unit testing bs
const exportFunctions = {
	loaded$,
	goodEnough,
	getSnapshotChildCount$,
};

export default exportFunctions;
